import ExcelJS from "exceljs";

export function convertToCSV(objArray) {
  if (!Array.isArray(objArray) || objArray.length === 0) return "";

  const headers = Object.keys(objArray[0]);
  const escape = (val) => {
    let str = val == null ? "" : String(val);
    if (str.includes('"')) str = str.replace(/"/g, '""');
    return `"${str}"`;
  };

  let csv = headers.map(escape).join(",") + "\r\n";

  objArray.forEach((row) => {
    const line = headers.map((key) => escape(row[key])).join(",");
    csv += line + "\r\n";
  });

  return csv;
}

/**
 * Export transaction counts by hour for different order types as CSV.
 *
 * @param {Object[]} transactions            - Array of transaction objects
 * @param {string}   selectedStoreCode       - Store code or 'All Stores'
 * @param {string}   selectedDate            - Date string for the report
 * @param {string}   [fileName='TotalTransactions.csv']
 */
// export function exportTransactionTypesByHourCSV(
//   transactions,
//   storess,
//   selectedDate,
//   transactionCounts,
//   chartData,
//   fileName = "TotalTransactions.csv"
// ) {
//   // 1) tally counts per hour for each transaction type
//   const counts = transactionCounts;

//   // 2) assume chartData.labels are hours 5:00 to 4:00 next day
//   //   const chartData = chartData;
//   const rows = chartData?.labels.map((day, index) => {
//     return {
//       Brand: "Angels Pizza", // Replace with actual brand logic
//       Store: storess || "All Stores",
//       Date:
//         selectedDate?.tran_date?.$gte && selectedDate?.tran_date?.$lte
//           ? `${new Date(selectedDate.tran_date.$gte).toLocaleDateString(
//               "en-US",
//               {
//                 year: "numeric",
//                 month: "long",
//                 day: "numeric",
//               }
//             )} - ${new Date(selectedDate.tran_date.$lte).toLocaleDateString(
//               "en-US",
//               {
//                 year: "numeric",
//                 month: "long",
//                 day: "numeric",
//               }
//             )}`
//           : "No date selected", // Or the date that corresponds to the 'day'
//       DineIn: transactionCounts.DineIn[index],
//       GrabFood: transactionCounts.GrabFood[index],
//       TakeOut: transactionCounts.TakeOut[index],
//       Delivery: transactionCounts.Delivery[index],
//       Drive: transactionCounts.Drive[index],
//       FoodPanda: transactionCounts.FoodPanda[index],
//     };
//   });

//   // 3) convert to CSV and trigger download
//   const csvString = convertToCSV(rows);
//   if (!csvString) {
//     console.warn("exportTransactionTypesByHourCSV: no data to export");
//     return;
//   }

//   const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
//   const link = document.createElement("a");
//   link.href = URL.createObjectURL(blob);
//   link.download = fileName;
//   document.body.appendChild(link);
//   link.click();
//   document.body.removeChild(link);
// }

export async function exportTransactionTypesByHourCSV(
  transactions,
  storess,
  selectedDate,
  transactionCounts,
  chartData,
  fileName = "TotalTransactions.xlsx",
) {
  const labels = chartData?.labels || [];
  const counts = transactionCounts || {};

  if (!labels.length) {
    console.warn("exportTransactionTypesByHourXLSX: no labels/data to export");
    return;
  }

  const dateLabel =
    selectedDate?.tran_date?.$gte && selectedDate?.tran_date?.$lte
      ? `${new Date(selectedDate.tran_date.$gte).toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
        })} - ${new Date(selectedDate.tran_date.$lte).toLocaleDateString(
          "en-US",
          {
            year: "numeric",
            month: "long",
            day: "numeric",
          },
        )}`
      : "No date selected";

  // Build rows
  const rows = labels.map((hourLabel, index) => {
    const DineIn = Number(counts.DineIn?.[index] ?? 0);
    const GrabFood = Number(counts.GrabFood?.[index] ?? 0);
    const TakeOut = Number(counts.TakeOut?.[index] ?? 0);
    const Delivery = Number(counts.Delivery?.[index] ?? 0);
    const Drive = Number(counts.Drive?.[index] ?? 0);
    const FoodPanda = Number(counts.FoodPanda?.[index] ?? 0);

    return {
      Brand: "Angels Pizza",
      Store: storess || "All Stores",
      Date: dateLabel,
      Hour: hourLabel,
      DineIn,
      GrabFood,
      TakeOut,
      Delivery,
      Drive,
      FoodPanda,
      Total: DineIn + GrabFood + TakeOut + Delivery + Drive + FoodPanda,
    };
  });

  const workbook = new ExcelJS.Workbook();
  workbook.creator = "Analytics Export";
  workbook.created = new Date();

  const sheet = workbook.addWorksheet("Transactions by Hour", {
    views: [{ state: "frozen", ySplit: 4 }],
  });

  // ===== Title bar =====
  sheet.mergeCells("A1:K1");
  const titleCell = sheet.getCell("A1");
  titleCell.value = "Total Transaction vs. Time of Day";
  titleCell.font = { bold: true, size: 16, color: { argb: "FFFFFFFF" } };
  titleCell.alignment = { vertical: "middle", horizontal: "left" };
  titleCell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FF1F2937" },
  };
  sheet.getRow(1).height = 26;

  // ===== Meta bar =====
  sheet.mergeCells("A2:K2");
  const metaCell = sheet.getCell("A2");
  metaCell.value = `Brand: Angels Pizza   |   Store: ${
    storess || "All Stores"
  }   |   Date: ${dateLabel}`;
  metaCell.font = { italic: true, size: 11, color: { argb: "FF111827" } };
  metaCell.alignment = { vertical: "middle", horizontal: "left" };
  metaCell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFF3F4F6" },
  };
  sheet.getRow(2).height = 18;

  // Spacer
  sheet.addRow([]);
  sheet.getRow(3).height = 6;

  // ===== Table header + data =====
  const headers = [
    "Brand",
    "Store",
    "Date",
    "Hour",
    "Dine In",
    "GrabFood",
    "Take Out",
    "Delivery",
    "Drive",
    "FoodPanda",
    "Total",
  ];

  sheet.addRow(headers);

  rows.forEach((r) => sheet.addRow(Object.values(r)));

  // Column widths
  sheet.columns = [
    { width: 16 }, // Brand
    { width: 18 }, // Store
    { width: 28 }, // Date
    { width: 12 }, // Hour
    { width: 12 },
    { width: 12 },
    { width: 12 },
    { width: 12 },
    { width: 10 },
    { width: 12 },
    { width: 10 },
  ];

  const headerRowIndex = 4;
  const firstDataRow = headerRowIndex + 1;
  const lastDataRow = headerRowIndex + rows.length;

  // Style header row
  const headerRow = sheet.getRow(headerRowIndex);
  headerRow.height = 18;
  headerRow.eachCell((cell) => {
    cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
    cell.alignment = {
      vertical: "middle",
      horizontal: "center",
      wrapText: true,
    };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF2563EB" },
    }; // blue
    cell.border = {
      top: { style: "thin", color: { argb: "FFE5E7EB" } },
      left: { style: "thin", color: { argb: "FFE5E7EB" } },
      bottom: { style: "thin", color: { argb: "FFE5E7EB" } },
      right: { style: "thin", color: { argb: "FFE5E7EB" } },
    };
  });

  // Style body rows (striped)
  for (let r = firstDataRow; r <= lastDataRow; r++) {
    const row = sheet.getRow(r);
    const isAlt = (r - firstDataRow) % 2 === 1;

    row.eachCell((cell, colNumber) => {
      // Borders
      cell.border = {
        top: { style: "thin", color: { argb: "FFE5E7EB" } },
        left: { style: "thin", color: { argb: "FFE5E7EB" } },
        bottom: { style: "thin", color: { argb: "FFE5E7EB" } },
        right: { style: "thin", color: { argb: "FFE5E7EB" } },
      };

      // Alignment + number formats
      if (colNumber <= 4) {
        cell.alignment = { vertical: "middle", horizontal: "left" };
      } else {
        cell.alignment = { vertical: "middle", horizontal: "center" };
        cell.numFmt = "#,##0";
      }

      // Alternate background
      if (isAlt) {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFF9FAFB" },
        };
      }
    });
  }

  // Totals row
  const totalsRowIndex = lastDataRow + 1;
  sheet.getRow(totalsRowIndex).height = 18;
  sheet.mergeCells(`A${totalsRowIndex}:D${totalsRowIndex}`);

  const totalLabelCell = sheet.getCell(`A${totalsRowIndex}`);
  totalLabelCell.value = "TOTAL";
  totalLabelCell.font = { bold: true };
  totalLabelCell.alignment = { vertical: "middle", horizontal: "left" };
  totalLabelCell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFEFF6FF" },
  };

  // Sum columns E..K (5..11)
  const colLetters = ["E", "F", "G", "H", "I", "J", "K"];
  colLetters.forEach((col) => {
    const cell = sheet.getCell(`${col}${totalsRowIndex}`);
    cell.value = {
      formula: `SUM(${col}${firstDataRow}:${col}${lastDataRow})`,
    };
    cell.font = { bold: true };
    cell.alignment = { vertical: "middle", horizontal: "center" };
    cell.numFmt = "#,##0";
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFEFF6FF" },
    };
    cell.border = {
      top: { style: "thin", color: { argb: "FF93C5FD" } },
      left: { style: "thin", color: { argb: "FF93C5FD" } },
      bottom: { style: "thin", color: { argb: "FF93C5FD" } },
      right: { style: "thin", color: { argb: "FF93C5FD" } },
    };
  });

  // Auto-filter on header
  sheet.autoFilter = {
    from: { row: headerRowIndex, column: 1 },
    to: { row: headerRowIndex, column: headers.length },
  };

  // ===== Download without file-saver =====
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Clean up object URL
  setTimeout(() => URL.revokeObjectURL(link.href), 500);
}
